import { Dispatch, FC, SetStateAction } from 'react';

import { Pressable, StyleSheet } from 'react-native';

import { FullWidthAutoHeightImage } from 'components/picture';
import { Box, Typography } from 'components/ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

const earnCrownsImage = require('./earn-crowns.png');
const exclusiveDealsImage = require('./exclusive-deals.png');
const mobileHero = require('./not-royal-perks-mobile.png');
const desktopHero = require('./not-royal-perks.png');
const redeemFreeBkImage = require('./redeem-free-bk.png');

interface WalmartRoyalPerksMemberV2Props {
  handleShouldRedirect?: Dispatch<SetStateAction<boolean>>;
}

export const WalmartRoyalPerksMemberV2: FC<WalmartRoyalPerksMemberV2Props> = ({
  handleShouldRedirect,
}) => {
  const isMobile = useIsMobileBreakpoint();
  const { setModalAuthState } = useAuthContext();

  const handleJoinNow = () => {
    setModalAuthState({
      screen: ModalAuthScreen.SIGN_IN,
      trigger: ModalAuthTrigger.Global,
      onSignInSuccess: () => handleShouldRedirect?.(true),
    });
  };

  return (
    <>
      <Typography
        variant="headerThree"
        fontSize={isMobile ? 'lg' : '2xl'}
        textAlign="center"
        style={[styles.sectionTitle, isMobile && styles.mobileSectionTitle]}
      >
        {t('notARoyalPerksMember')}
      </Typography>
      <Box style={[styles.container, isMobile && styles.mobileContainer]}>
        <FullWidthAutoHeightImage
          source={isMobile ? mobileHero : desktopHero}
          style={styles.hero}
          contentFit="contain"
        />
        <Typography
          variant="headerThree"
          fontSize="2xl"
          textAlign="center"
          bold
          style={[styles.title, isMobile && styles.mobileTitle]}
        >
          {t('walmartRoyalPerksSignUpTitle', {
            defaultValue: 'Sign up for Royal Perks {separator}and Save',
            separator: isMobile ? '\n' : '',
          })}
        </Typography>
        <Typography
          variant="copyTwo"
          fontSize="md"
          textAlign="center"
          style={[styles.subtitle, isMobile && styles.mobileSubtitle]}
        >
          {t('walmartRoyalPerksBestDeals', 'Get the best deals, every day.')}
        </Typography>
        <Box style={[styles.itemsContainer, isMobile && styles.mobileItemsContainer]}>
          <Benefit
            image={earnCrownsImage}
            label={t('earnCrownsV2', {
              defaultValue: 'Earn {separator}Crowns',
              separator: isMobile ? '\n' : '',
            })}
          />
          <Benefit
            image={exclusiveDealsImage}
            label={t('exclusiveDealsV2', {
              defaultValue: 'Exclusive {separator}Deals',
              separator: isMobile ? '\n' : '',
            })}
          />
          <Benefit
            image={redeemFreeBkImage}
            label={t('redeemFreeBkV2', {
              defaultValue: 'Redeem {separator}Free BK®',
              separator: isMobile ? '\n' : '',
            })}
          />
        </Box>
        <Box style={styles.joinButtonContainer}>
          <Pressable
            accessibilityRole="button"
            onPress={handleJoinNow}
            style={[styles.joinButton, isMobile && styles.mobileJoinButton]}
          >
            <Typography fontSize="md" textAlign="center" style={styles.joinButtonText}>
              {t('walmartRoyalPerksJoinNow', 'Join Now')}
            </Typography>
          </Pressable>
        </Box>
      </Box>
    </>
  );
};

interface BenefitProps {
  image: number;
  label: string;
}

const Benefit: FC<BenefitProps> = ({ image, label }) => (
  <Box style={styles.itemContainer}>
    <FullWidthAutoHeightImage source={image} contentFit="contain" style={styles.benefitImage} />
    <Typography fontSize="sm" style={styles.itemText}>
      {label}
    </Typography>
  </Box>
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$white,
    borderRadius: spacing.$3,
    overflow: 'hidden',
    paddingHorizontal: spacing.$5,
    paddingTop: spacing.$5,
    paddingBottom: spacing.$9,
    width: '100%',
  },
  mobileContainer: {
    borderRadius: spacing.$3,
    paddingHorizontal: spacing.$3,
    paddingTop: spacing.$3,
    paddingBottom: spacing.$6,
  },
  hero: { width: '100%' },
  title: {
    color: primitive.$bbqBrown,
    marginTop: spacing.$5,
    marginBottom: spacing.$2,
    lineHeight: spacing.$7,
  },
  sectionTitle: { marginBottom: spacing.$10, marginTop: spacing.$12 },
  mobileSectionTitle: { marginBottom: spacing.$6, marginTop: spacing.$6 },
  mobileTitle: { marginTop: spacing.$4 },
  subtitle: { color: primitive.$bbqBrown, marginBottom: spacing.$6 },
  mobileSubtitle: { marginBottom: spacing.$4 },
  itemsContainer: {
    alignSelf: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    maxWidth: 440,
    width: '100%',
  },
  itemContainer: { alignItems: 'center', justifyContent: 'center', flex: 1 },
  mobileItemsContainer: { maxWidth: 340 },
  benefitImage: { height: 64, width: 64 },
  itemText: {
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    marginTop: spacing.$2,
    textAlign: 'center',
  },
  joinButtonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  joinButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: primitive.$meltyYellow,
    borderRadius: spacing.$8,
    marginTop: spacing.$5,
    width: 136,
    paddingVertical: spacing.$3,
  },
  mobileJoinButton: {
    marginTop: spacing.$4,
  },
  joinButtonText: {
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    lineHeight: spacing.$4,
  },
});
